import type { JSX } from "react";
import { cn } from "@/lib/utils";

/**
 * A rotating lines loading spinner component that mirrors react-loader-spinner's RotatingLines
 * with customizable props for width, stroke color, stroke width, and animation duration.
 *
 * @component
 * @example
 * <RotatingLinesLoader
 *   width="60"
 *   strokeColor="#42A7DC"
 *   strokeWidth="5"
 *   animationDuration="0.75"
 *   ariaLabel="rotating-lines-loading"
 * />
 */
interface RotatingLinesLoaderProps {
  /** Whether the loader is visible */
  visible?: boolean;
  /** Width of the loader in pixels */
  width?: string | number;
  /** Color of the rotating lines */
  strokeColor?: string;
  /** Width of the stroke lines */
  strokeWidth?: string | number;
  /** Duration of the rotation animation in seconds */
  animationDuration?: string | number;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Additional CSS class names */
  className?: string;
}

const RotatingLinesLoader = ({
  visible = true,
  width = 96,
  strokeColor = "#94A3B8",
  strokeWidth = 5,
  animationDuration = 0.75,
  ariaLabel = "rotating-lines-loading",
  className = "",
}: RotatingLinesLoaderProps): JSX.Element | null => {
  if (!visible) return null;

  const size = typeof width === "number" ? `${width}px` : width;
  const strokeWidthValue = typeof strokeWidth === "number" ? strokeWidth : parseFloat(strokeWidth.toString());
  const animationDurationValue = typeof animationDuration === "number" ? animationDuration : parseFloat(animationDuration.toString());

  // Generate unique ID for this instance to avoid conflicts
  const uniqueId = `rotating-lines-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <>
      <div
        role="status"
        aria-label={ariaLabel}
        className={cn("inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid"
        >
          {/* Create 12 lines arranged in a circle */}
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i * 30) * (Math.PI / 180); // Convert to radians
            const x1 = 50 + Math.cos(angle) * 35;
            const y1 = 50 + Math.sin(angle) * 35;
            const x2 = 50 + Math.cos(angle) * 20;
            const y2 = 50 + Math.sin(angle) * 20;

            return (
              <line
                key={i}
                x1={x2}
                y1={y2}
                x2={x1}
                y2={y1}
                stroke={strokeColor}
                strokeWidth={strokeWidthValue}
                strokeLinecap="round"
                className={cn(`${uniqueId}-line-${i}`)}
              />
            );
          })}
        </svg>
        <span className="absolute! -m-px! h-px! w-px! overflow-hidden! whitespace-nowrap! border-0! p-0! [clip:rect(0,0,0,0)!]">
          Loading...
        </span>
      </div>

      <style>{`
        @keyframes ${uniqueId}-fade {
          0%, 39%, 100% { opacity: 0.25; }
          40% { opacity: 1; }
        }

        ${Array.from({ length: 12 }, (_, i) => `
          .${uniqueId}-line-${i} {
            animation: ${uniqueId}-fade ${animationDurationValue}s ease-in-out infinite;
            animation-delay: ${(i * animationDurationValue / 12).toFixed(3)}s;
          }
        `).join("")}
      `}</style>
    </>
  );
};

export default RotatingLinesLoader;
